'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useStoreHref } from '@/components/store';
import type { NavigationItem } from '@cobrastyle/shared-types';
import {
  getSearchSuggestions,
  type SearchSuggestionsResult,
} from '@cobrastyle/storefront-core/actions';
import { Spinner } from '@cobrastyle/ui';
import type { BrandMenu } from './navigation';
import { searchBrands, type BrandSuggestion } from '@/actions/brand-search';
import { fetchProductBrands } from '@/actions/product-brand';
import type { PopularSearchTerm } from '@/lib/popular-search-terms';
import { useRecentSearches } from '@/lib/use-recent-searches';
import { MIN_QUERY_LENGTH } from '@/lib/search-constants';
import { MobileSearchPanel } from './mobile-search';
import { useMobileOverlay } from './mobile-overlay-context';

interface MobileSearchTriggerProps {
  categories?: NavigationItem[];
  brandMenu?: BrandMenu | null;
  popularSearchTerms?: PopularSearchTerm[];
}

/**
 * The mobile header's search bar — a real input, always in place (not a
 * fake button that gets replaced by a full-screen takeover). Typing opens a
 * panel below it, same as the hamburger drawer, so the header (logo,
 * wishlist, cart) stays visible and reachable while searching, matching the
 * design canvas's own mobile search screen.
 */
export function MobileSearchTrigger({
  categories = [],
  brandMenu,
  popularSearchTerms = [],
}: MobileSearchTriggerProps) {
  const tSearch = useTranslations('search');
  const tc = useTranslations('common');
  const router = useRouter();
  const storePath = useStoreHref();

  const [query, setQuery] = useState('');
  // Mirrors the desktop search bar: only updated once new content is ready —
  // see search-bar.tsx for the full rationale.
  const [committedQuery, setCommittedQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestionsResult | null>(null);
  const [brandMatches, setBrandMatches] = useState<BrandSuggestion[]>([]);
  const [productBrands, setProductBrands] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { activeOverlay, openOverlay, closeOverlay } = useMobileOverlay();
  const isOpen = activeOverlay === 'search';
  const { recentSearches, saveRecentSearch, clearRecentSearch, clearAllRecentSearches } =
    useRecentSearches();

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < MIN_QUERY_LENGTH) {
      setCommittedQuery(searchQuery);
      setSuggestions(null);
      setBrandMatches([]);
      setProductBrands({});
      return;
    }

    setIsLoading(true);
    try {
      const [result, brands, brandsBySku] = await Promise.all([
        getSearchSuggestions(searchQuery),
        searchBrands(searchQuery),
        fetchProductBrands(searchQuery),
      ]);
      setSuggestions(result);
      setBrandMatches(brands);
      setProductBrands(brandsBySku);
      setCommittedQuery(searchQuery);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Lock body scroll while the panel is open (mirrors MobileNavigation's drawer).
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const close = () => {
    closeOverlay();
    inputRef.current?.blur();
  };

  const goToResults = (term: string) => {
    saveRecentSearch(term);
    router.push(storePath(`/sok?q=${encodeURIComponent(term)}`));
    close();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) goToResults(query.trim());
  };

  // Same redirect-first handling as the desktop search bar: an admin-set
  // destination on the term wins over running a search for its text.
  const handlePopularTermClick = (term: PopularSearchTerm) => {
    if (term.redirect) {
      const path = term.redirect.startsWith('/') ? term.redirect : `/${term.redirect}`;
      close();
      router.push(storePath(path));
      return;
    }
    goToResults(term.text);
  };

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="flex items-center gap-3">
        <div className="relative flex-1">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => openOverlay('search')}
            placeholder={tSearch('headerPlaceholder')}
            className="w-full rounded-sm border border-border bg-card py-[11px] pl-10 pr-9 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors focus:border-accent-600 focus:ring-1 focus:ring-ring focus:ring-offset-0"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Spinner size="sm" className="text-muted" />
            </div>
          )}
        </div>
        {isOpen && (
          <button
            type="button"
            onClick={close}
            className="shrink-0 text-sm text-muted transition-colors hover:text-foreground"
          >
            {tc('cancel')}
          </button>
        )}
      </form>

      {isOpen && (
        <MobileSearchPanel
          committedQuery={committedQuery}
          suggestions={suggestions}
          brandMatches={brandMatches}
          productBrands={productBrands}
          recentSearches={recentSearches}
          saveRecentSearch={saveRecentSearch}
          clearRecentSearch={clearRecentSearch}
          clearAllRecentSearches={clearAllRecentSearches}
          categories={categories}
          brandMenu={brandMenu}
          popularSearchTerms={popularSearchTerms}
          onClose={close}
          onPopularTermClick={handlePopularTermClick}
          onGoToResults={goToResults}
        />
      )}
    </div>
  );
}
